'use client';

import { useEffect } from 'react';
// global-error.tsx replaces the root layout when it renders, so it has to
// bring its own <html>/<body> *and* its own stylesheet — nothing from
// app/layout.tsx applies here.
// https://nextjs.org/docs/app/api-reference/file-conventions/error#global-error
import '../styles/globals.css';
import * as Sentry from '@sentry/nextjs';
import ErrorScreen from 'components/shared/layout/ErrorScreen';
/**
 * The root-level error boundary, for exceptions thrown in the root layout itself —
 * where `app/error.tsx` cannot catch them. Carries the same `pages/_error.tsx` copy,
 * image and Sentry capture (#489, FR-010), but must render its own <html>/<body>
 * because it replaces the layout.
 */
const GlobalError = ({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) => {
  useEffect(() => {
    // Mirrors the reporting in pages/_error.tsx. No Sentry.flush() here: this
    // boundary only ever renders in the browser, where the transport is not
    // torn down the way a serverless invocation is.
    Sentry.captureException(error);

    // Keep the console.log here as Sentry is not actively using by AceHub
    console.error(error);
  }, [error]);

  return (
    <html lang="en">
      <body>
        <ErrorScreen description="An error occurred while loading this page.">
          <button
            type="button"
            onClick={() => reset()}
            className="mt-6 rounded-full border border-black px-6 py-2 text-lg font-thin text-black dark:border-white dark:text-white"
          >
            Try again
          </button>
        </ErrorScreen>
      </body>
    </html>
  );
};

export default GlobalError;
